import { NotificationNavBadge } from '@/features/notifications/useNotificationFeed';
import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { motion } from 'motion/react';
import { House, Award, Lightbulb, Package, Ellipsis } from 'lucide-react';
import { Drawer, Tooltip } from '@/components/ui';
import { brandingStyle } from '@/lib/branding';
import { cx } from '@/lib/cx';
import { useMediaQuery } from '@/lib/hooks';
import { PlatformBanner } from '@/lib/platformAppearance';
import { useSession } from '@/lib/session';
import { useTheme } from '@/lib/theme';
import { AnnouncementBar } from './AnnouncementBar';
import { NotificationsBell } from './NotificationsBell';
import { UpdatesPanel } from './UpdatesPanel';
import { UserMenu } from './UserMenu';
import { AppHeader } from './AppHeader';
import { PageSkeleton } from './StaffShell';
import { portalNavigation } from './portalNavigation';
const MORE_LINKS = [
  { to: '/portal/more/products', label: 'Our Other Products', icon: Package },
  { to: '/portal/more/suggest-feature', label: 'Suggest Feature', icon: Lightbulb },
  { to: '/portal/more/gamification', label: 'Badges & XP', icon: Award },
];

export function PortalShell() {
  const { principal, impersonation, can } = useSession();
  const { resolved } = useTheme();
  const location = useLocation();
  const desktop = useMediaQuery('(min-width: 1024px)');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);
  const branding = principal?.branding;
  const brandName = branding?.displayName || principal?.tenantName || 'Onboarding';
  const { links, description } = portalNavigation(can);
  const tabLinks = links.filter(item => item.to !== '/portal/notifications');
  const workspaceLabel = principal?.roleLabel ?? 'Client portal';
  const sidebar = (compact: boolean) => <aside className="sidebar" aria-label="Client navigation">
    <NavLink to="/portal" className="sidebar-head portal-brand" onClick={() => setMobileOpen(false)} aria-label={`${brandName} overview`}>
      {branding?.logoUrl ? <img className="app-brand-logo" src={branding.logoUrl} alt="" /> : <span className="workspace-mark" aria-hidden><House size={22} /></span>}
      <div className="workspace-name"><strong>{brandName}</strong><span>{principal?.roleLabel ?? 'Client portal'}{impersonation ? ' | Read-only' : ''}</span></div>
    </NavLink>
    <nav className="nav" aria-label="Client pages"><div className="nav-group"><div className="nav-group-label">{workspaceLabel}</div>
      {!compact && <p className="t-caption muted" style={{ padding: '0 var(--space-3)', marginBottom: 'var(--space-3)' }}>{description}</p>}
      {links.map(item => {
        const link = <NavLink key={item.to} to={item.to} end={item.end} aria-label={item.label} className="nav-link" onClick={() => setMobileOpen(false)}><item.icon aria-hidden /><span>{item.label}</span>{item.to.endsWith('/notifications') && <NotificationNavBadge />}</NavLink>;
        return compact ? <Tooltip key={item.to} content={item.label} side="right">{link}</Tooltip> : link;
      })}
    </div><div className="nav-group"><div className="nav-group-label">More</div>{MORE_LINKS.map(item => {
      const link = <NavLink key={item.to} to={item.to} aria-label={item.label} className="nav-link" onClick={() => setMobileOpen(false)}><item.icon aria-hidden /><span>{item.label}</span></NavLink>;
      return compact ? <Tooltip key={item.to} content={item.label} side="right">{link}</Tooltip> : link;
    })}</div></nav>
    <div className="sidebar-foot"><UserMenu variant="chip" /></div>
  </aside>;
  return <div className={cx('shell app-shell client-shell', desktop && collapsed && 'is-collapsed')} style={brandingStyle(branding, resolved)}>
    <a href="#main" className="skip-link">Skip to content</a>
    {desktop && sidebar(collapsed)}
    <div className="main">
      <PlatformBanner />
      <AppHeader desktop={desktop} context={brandName} groups={[{ label: workspaceLabel, items: links }, { label: 'More', items: MORE_LINKS }]} onToggleNavigation={() => desktop ? setCollapsed(!collapsed) : setMobileOpen(true)} navigationExpanded={desktop ? !collapsed : mobileOpen} tools={<><UpdatesPanel /><NotificationsBell /></>} />
      <main id="main" tabIndex={-1} className="content client-content">
        <AnnouncementBar />
        <motion.div key={location.pathname} className="route-fade" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .18 }}>
          <Suspense fallback={<PageSkeleton />}><Outlet /></Suspense>
        </motion.div>
      </main>
    </div>
    {!desktop && <Drawer className="app-mobile-navigation" open={mobileOpen} onOpenChange={setMobileOpen} title="Navigation" side="left" size="sm">{sidebar(false)}</Drawer>}
    <nav className="portal-tabbar" aria-label="Portal" style={{ gridTemplateColumns: `repeat(${tabLinks.length + 1}, minmax(0, 1fr))` }}>{tabLinks.map(item => <NavLink key={item.to} to={item.to} end={item.end}><item.icon aria-hidden /><span>{item.short ?? item.label}</span></NavLink>)}<NavLink to="/portal/more"><Ellipsis aria-hidden /><span>More</span></NavLink></nav>
  </div>;
}
